import env from 'bench-environment';
import emailRegExpFromMainApp from './emailRegExpFromMainApp';

// Uses the signup funnel's email validation endpoint
const getCheckAccountAvailabilityURI = email =>
  `${env.location.apiRoot}/api/v1/signup.json/account_available?email=${encodeURIComponent(email)}`;

/**
 * Creates a validation function for checking the uniqueness of a user's email. The returned
 * function takes an email address as input and returns a Promise. The Promise resolves to a
 * validation error message. If the email has a valid format and is unique, the validation error
 * message is the empty string, indicating "no errors". Otherwise the error message indicates why
 * the email is invalid.
 */
const uniquenessOfEmail = () => {
  // To prevent race conditions between validations that are triggered close to each other, each
  // network request to validate the email address is cancelled before initiating the next one. The
  // controller needs to be accessible to subsequent invocations of the validation function which is
  // why this closure is necessary.
  let abortController;
  return email => {
    // If a previous network request is in progress, it should be aborted before validating
    if (abortController) {
      abortController.abort();
    }
    // If the input is empty, then it's considered valid. A separate, optional `required` validation
    // is responsible for restricting the submission of empty values.
    if (!email) {
      return Promise.resolve('');
    }
    // Validate the format of the email client-side instead of making a network request that will
    // give a known response
    if (!emailRegExpFromMainApp.test(email)) {
      return Promise.resolve('E-mail is invalid.');
    }
    // AbortController is still "experimental", so make sure it's available before invoking it
    if (typeof AbortController === 'function') {
      // eslint-disable-next-line no-undef
      abortController = new AbortController();
    }
    return (
      fetch(getCheckAccountAvailabilityURI(email), abortController)
        .then(res => res.json())
        // If there's a message, then the email is invalid, otherwise it's valid. To mark something
        // as valid, its validation message must be the empty string.
        .then(({ message }) => {
          if (message)
            return message.replace(
              'An account with that email address already exists.',
              'A user with this email already exists. Exit this modal, ' +
                'search for this email, and assign the existing user instead of creating a new user.',
            );
          return '';
        })
        // If the network request failed, consider the input valid to not block form submission
        .catch(() => '')
    );
  };
};

export default uniquenessOfEmail;
